import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Input, Button } from '@lomadee/design-system';
import { Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';

import { MARY_KAY_WHITELABEL, OLIST_HOST_NAME } from '@plug/config/consts';
import { Spacer } from '@plug/ui';
import { Flex } from '@plug/ui2/rebass';
import { useCreateAccountContext } from '@plug/contexts/CreateAccountContext';
import { AffiliateRegisterNewPasswordSchema } from './AffiliateRegistrationPassword.schema';
import { TitleForm } from '../../style';

const AffiliateRegistrationPasswordStep: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const { values: contextValues, setStep, addValues } = useCreateAccountContext();

  const isOlist = OLIST_HOST_NAME.includes(window.location.hostname);

  const [showPasswordInputs, setShowPasswordInputs] = useState({
    password: false,
    passwordConfirmation: false,
  });

  const toggleShowPasswordInput = (passwordInput: keyof typeof showPasswordInputs): void => {
    setShowPasswordInputs({
      ...showPasswordInputs,
      [passwordInput]: !showPasswordInputs[passwordInput],
    });
  };

  const handleSubmitFormik = (formikValues: CreateAccountFormI): void => {
    addValues(formikValues);
    setStep('PERSONAL_DATA');
  };

  const getPageTitle = () => {
    if (OLIST_HOST_NAME.includes(window.location.hostname)) {
      return 'Para começar, escolha uma senha.';
    }

    if (MARY_KAY_WHITELABEL.includes(window.location.hostname)) {
      return (
        <div>
          Vamos lá? <br /> Para começar, escolha sua senha
        </div>
      );
    }

    return <Trans i18nKey="hubly-dashboard.signup.affiliate.right-side.first-step.title" />;
  };

  const pageTitle = getPageTitle();

  return (
    <Flex flexDirection="column" sx={{ gap: '16px' }}>
      {!isMobile && <Spacer mt="80px" />}

      <TitleForm>{pageTitle}</TitleForm>

      {MARY_KAY_WHITELABEL.includes(window.location.hostname) && (
        <Flex
          alignItems="center"
          justifyContent="center"
          backgroundColor={'#faedef'}
          borderRadius="8px"
        >
          <span
            className="items-center"
            style={{ padding: '16px', fontSize: '14px', lineHeight: '18px', color: '#333333' }}
          >
            <b style={{ color: '#d00070' }}>IMPORTANTE:</b> Consultora, lembre-se de memorizar a sua
            senha para usá-la em seus próximos acessos.
          </span>
        </Flex>
      )}

      <Formik
        initialValues={contextValues}
        validationSchema={AffiliateRegisterNewPasswordSchema}
        validateOnMount={true}
        onSubmit={handleSubmitFormik}
      >
        {({
          values,
          handleChange,
          errors,
          isValid,
          handleSubmit,
          handleBlur,
          touched,
        }): JSX.Element => (
          <>
            <Input
              label={
                isOlist
                  ? 'Senha*'
                  : t('hubly-dashboard.signup.affiliate.right-side.first-step.form.password.label')
              }
              placeholder={t(
                'hubly-dashboard.signup.affiliate.right-side.first-step.form.password.placeholder',
              )}
              name="password"
              value={values.password}
              onBlur={handleBlur}
              onChange={handleChange}
              error={touched.password && !!errors.password}
              type={showPasswordInputs.password ? 'text' : 'password'}
              helperText={
                !touched.password && errors.password
                  ? t('hubly-dashboard.signup.affiliate.right-side.first-step.form.password.helper')
                  : touched.password && errors.password
                  ? errors.password
                  : ''
              }
              iconRight={showPasswordInputs.password ? 'eyeSlash' : 'eye'}
              iconRightOnClick={(): void => toggleShowPasswordInput('password')}
              iconRightPadding="LG"
              fluid
            />

            <Flex mt="8px">
              <Input
                label={
                  isOlist
                    ? 'Confirmar senha*'
                    : t(
                        'hubly-dashboard.signup.affiliate.right-side.first-step.form.confirm-password.label',
                      )
                }
                placeholder={t(
                  'hubly-dashboard.signup.affiliate.right-side.first-step.form.confirm-password.placeholder',
                )}
                name="passwordConfirmation"
                value={values.passwordConfirmation}
                onBlur={handleBlur}
                onChange={handleChange}
                error={touched.passwordConfirmation && !!errors.passwordConfirmation}
                helperText={touched.passwordConfirmation ? errors.passwordConfirmation : ''}
                type={showPasswordInputs.passwordConfirmation ? 'text' : 'password'}
                iconRight={showPasswordInputs.passwordConfirmation ? 'eyeSlash' : 'eye'}
                iconRightOnClick={(): void => toggleShowPasswordInput('passwordConfirmation')}
                iconRightPadding="LG"
                fluid
              />
            </Flex>

            <Flex marginTop={'20px'}>
              <Button fluid type="submit" disabled={!isValid} onClick={(): void => handleSubmit()}>
                {t(
                  'hubly-dashboard.signup.affiliate.right-side.first-step.form.buttons.confirm.text',
                )}
              </Button>
            </Flex>
          </>
        )}
      </Formik>
    </Flex>
  );
};

export default AffiliateRegistrationPasswordStep;
